import { Component, OnInit, OnDestroy, ViewChild, Input, Renderer2, Inject, signal, computed } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { debounceTime, firstValueFrom, lastValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { Design } from '../../../shared/models/design.model';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { WellConfigService } from '../../../shared/services/well-config.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { ChangeSelectedTubular, CompactModeChangedMessage, DashboardItemAddedMessage, GridItemResizedMessage, OpenSearchComponentDropdown, PanelModeChangedMessage, SetPanelColumnsMessage, ToggleCompactModeMessage, TogglePanelModeMessage, ToggleWellSchematicSidebarMessage, ToggleWizardModeMessage, WizardModeChangedMessage, WizardStepChangedMessage, WizardStepNavigateMessage } from 'src/app/shared/models/mediator-messages.model';
import { CalculationsApiService } from './calculations-api.service';
import { LauncherSharedService } from '../../services/launcher-shared.service';
import { UsersService } from '../../services/users-service';
import { Tubular } from 'src/app/wellbore-inputs/components/well-configuration/models';
import { User } from '../user-admin-page/user-model';
import { StorageKeys, SessionStorageKeys, StoreService } from '../../services/store.service';
import { Store, StoreDef } from '@ngneat/elf';
import { Select } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { UnitsConvertComponent } from '../units-convert/units-convert.component';
import { DOCUMENT } from '@angular/common';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';
import { ComponentSearchItem } from './toolbar-search.helper';
import { ToolbarActionsComponent, ComponentSelectedEvent } from './toolbar-actions.component';
import { UserUnitsModel } from '../user-units/user-units.model';
import { UnitPrecisionOverrides } from 'src/app/shared/helpers/unit-precision.helper';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styles: `
    :host ::ng-deep .p-toolbar {
      flex-wrap: nowrap !important;
      overflow: hidden;
    }

    :host ::ng-deep .p-toolbar-start {
      min-width: 0;
      flex-shrink: 1;
    }

    :host ::ng-deep .p-toolbar-end {
      flex-shrink: 0;
    }
  `,
  providers: [DialogService],
  standalone: false
})
export class ToolbarComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _subscriptions: Subscription = new Subscription();
  private _schematicOpen = false;
  private _appIds: CurrentAppIdsDto | null = null;
  private _signalRHub: any = null;
  private _signalRHandler!: (data: any) => Promise<void>;

  private readonly _selectedTubular = signal<Tubular | null>(null);

  public readonly tubulars = signal<Tubular[]>([]);
  public readonly shortLengthUnit = signal('in');
  public readonly pipeDimensionPrecisionOverrides = signal<UnitPrecisionOverrides>({ in: 3 });
  public readonly calculatingPerivis = signal(false);
  public readonly calculatingPerical = signal(false);
  public readonly calculatingPerinix = signal(false);
  public readonly currentDesign = signal<Design | null>(null);
  // public readonly isLoading = signal(false);
  public readonly isTubularReady = signal(false);
  public readonly components = signal<ComponentSearchItem[]>([]);
  public readonly compactMode = signal(true);
  public readonly panelMode = signal(false);
  public readonly wizardMode = signal(false);
  public readonly wizardStepIndex = signal(0);
  public readonly wizardTotalSteps = signal(0);
  public readonly wizardStepLabel = signal('');
  public readonly wizardStepLabels = signal<readonly string[]>([]);
  public readonly isDarkMode = signal(true);

  public readonly isPericalDisabled = computed(() =>
    this.calculatingPerical() || !this._selectedTubular() || !!this.user?.roles?.readOnly
  );
  public readonly isPerinixDisabled = computed(() =>
    this.calculatingPerinix() || this.calculatingPerical() || !this._selectedTubular() || !!this.user?.roles?.readOnly
  );
  public readonly isPerivisDisabled = computed(() =>
    this.calculatingPerivis() || !this._selectedTubular() || !!this.user?.roles?.readOnly
  );

  public panelColumnItems: MenuItem[] = [];
  private themeStore!: Store<StoreDef<{ isDarkMode: boolean }>>;

  @Input()
  public user: User | null = null;

  public get selectedTubular(): Tubular | null {
    return this._selectedTubular();
  }

  public set selectedTubular(value: Tubular | null) {
    this._selectedTubular.set(value);
  }

  @ViewChild(ToolbarActionsComponent)
  private _actionsEl!: ToolbarActionsComponent;

  constructor(
    private _launcherSharedService: LauncherSharedService,
    private _calcApiService: CalculationsApiService,
    private _userService: UsersService,
    private _wellConfigService: WellConfigService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _mediator: MediatorService,
    private _dialogService: DialogService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    const origOnKeydown = Select.prototype.onKeyDown;
    Select.prototype.onKeyDown = function (event: KeyboardEvent, search: boolean) {
      if (event.which == 9) {
        this.hide();
        return;
      }
      origOnKeydown.apply(this, [event, search]);
    };
  }

  ngOnInit(): void {
    void this.loadPipeDimensionPrecisionOverrides();

    this._storeService.watchSession<Design>(SessionStorageKeys.DESIGN)
      .pipe(takeUntil(this._destroy$))
      .subscribe(design => {
        this.currentDesign.set(design);
      });

    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(async appIds => {
        const designChanged = appIds?.designId !== this._appIds?.designId;
        const tubularChanged = appIds?.tubularId !== this._appIds?.tubularId;
        this._appIds = appIds;

        if (designChanged) {
          this.isTubularReady.set(false);
          this.calculatingPerinix.set(false);
          this.calculatingPerivis.set(false);
          this.calculatingPerical.set(false);
          await this.getStringList(true);
          this.isTubularReady.set(true);
          return;
        }

        if (tubularChanged) {
          this.setSelectedTubular(appIds?.tubularId);
        }
      });

    this._subscriptions.add(
      this._mediator.of(ChangeSelectedTubular).subscribe((e) => {
        if (!e.selectedTubular || e.selectedTubular?.id == this.selectedTubular?.id) {
          return;
        }
        this.onTubularChanged(e.selectedTubular);
      })
    );

    this._signalRHub = this._signalRService.getConnectionToNotificationHub();
    this._signalRHandler = async (data: any) => {
      if (this._appIds?.designId != data.designId) {
        return;
      }
      if (data.action == PeriforOnChangeMessages.REFRESH_TUBULARS_LIST) {
        this.getStringList(false);
      }
      if (data.action === PeriforOnChangeMessages.APB_CALCULATIONS_FINISHED) {
        this.calculatingPerinix.set(false);
      }
      if (data.action === PeriforOnChangeMessages.STRESS_CALCULATIONS_FINISHED) {
        this.calculatingPerivis.set(false);
      }
      if (data.action === PeriforOnChangeMessages.THERMAL_CALCULATIONS_FINISHED) {
        this.calculatingPerical.set(false);
      }
    };
    this._signalRHub.on(SignalRService.ON_PFB_CHANGE, this._signalRHandler);

    this._subscriptions.add(
      this._mediator.of(OpenSearchComponentDropdown)
        .pipe(debounceTime(100))
        .subscribe(() => {
          this._actionsEl?.showAndFocusSearch();
        })
    );

    this._subscriptions.add(
      this._mediator.of(CompactModeChangedMessage).subscribe(message => {
        this.compactMode.set(message.isEnabled);
        this.updatePanelColumnItems();
      })
    );

    this._subscriptions.add(
      this._mediator.of(PanelModeChangedMessage).subscribe(message => {
        this.panelMode.set(message.isEnabled);
        this.updatePanelColumnItems();
      })
    );

    this._subscriptions.add(
      this._mediator.of(WizardModeChangedMessage).subscribe(message => {
        this.wizardMode.set(message.isEnabled);
      })
    );

    this._subscriptions.add(
      this._mediator.of(WizardStepChangedMessage).subscribe(message => {
        this.wizardStepIndex.set(message.stepIndex);
        this.wizardTotalSteps.set(message.totalSteps);
        this.wizardStepLabel.set(message.label);
        this.wizardStepLabels.set(message.labels ?? []);
      })
    );

    this.themeStore = this._storeService.createStore(StorageKeys.DARK_MODE, {});

    this._subscriptions.add(
      this.themeStore.subscribe((v) => {
        this.isDarkMode.set(v?.isDarkMode ?? true);
      })
    );

    this.updatePanelColumnItems();

    this.getComponents();
  }

  private async loadPipeDimensionPrecisionOverrides(): Promise<void> {
    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    const shortLengthUnit = units?.shortLengths ?? 'in';
    this.shortLengthUnit.set(shortLengthUnit);
    this.pipeDimensionPrecisionOverrides.set({ [shortLengthUnit]: shortLengthUnit === 'in' ? 3 : 2 });
  }

  private getComponents() {
    this._launcherSharedService.getComponents().subscribe((data: Array<{ key: string; name: string; properties: string[] }> | null) => {
      const items: ComponentSearchItem[] = (data ?? []).map(cmp => ({
        key: cmp.key,
        name: cmp.name,
        properties: cmp.properties,
        displayLabel: `${cmp.name} (${cmp.properties.join(', ')}`
      }));
      this.components.set([...items].sort((a, b) => a.name.localeCompare(b.name)));

      if (data == null || data.length == 0) {
        this.getComponents();
      }
    });
  }

  async getStringList(updateSelectedTubular: boolean) {
    if (!this._appIds?.designId) {
      this.tubulars.set([]);
      this.selectedTubular = null;
      return;
    }

    // this.isLoading.set(true);

    const tubulars = await lastValueFrom(this._wellConfigService.getStringsList(this._appIds?.designId));
    this.tubulars.set(tubulars ?? []);

    if (updateSelectedTubular) {
      this.setSelectedTubular(this._appIds.tubularId);
    } else {
      const availableTubulars = this.tubulars();
      this.selectedTubular = availableTubulars.find(t => t.id === this._appIds?.tubularId) ?? availableTubulars[0] ?? null;
    }

    // this.isLoading.set(false);
  }

  public onComponentSelected(event: ComponentSelectedEvent): void {
    this._mediator.publish(new DashboardItemAddedMessage(event.key));

    setTimeout(() => {
      this.highlightLabel(event.matchedProperty);
    }, 1000);
  }

  highlightLabel(labelText: string | null) {
    if (labelText == null) {
      return;
    }
    const textElements: NodeListOf<HTMLElement> = this.document.querySelectorAll('label');
    const matchingElements: HTMLElement[] = [];

    textElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;

      if (isVisible && element.textContent?.trim().startsWith(labelText)) {
        matchingElements.push(element);
      }
    });

    matchingElements.forEach(element => {
      this.renderer.addClass(element, 'highlight');
      setTimeout(() => {
        this.renderer.removeClass(element, 'highlight');
      }, 3000);
    });
  }

  setSelectedTubular(tubularId?: string | null) {
    const availableTubulars = this.tubulars();
    this.selectedTubular = availableTubulars.find(t => t.id === tubularId) ?? availableTubulars[0] ?? null;
  }

  async onCalculateStress() {
    this.calculatingPerivis.set(true);
    setTimeout(async () => {
      await lastValueFrom(this._calcApiService.calculateStress());
    }, 100);
  }

  async onCalculateThermal() {
    this.calculatingPerical.set(true);
    setTimeout(async () => {
      await lastValueFrom(this._calcApiService.calculateThermal());
    }, 100);
  }

  async onCalculateApb() {
    this.calculatingPerinix.set(true);
    setTimeout(async () => {
      await lastValueFrom(this._calcApiService.calculateApb());
    }, 100);
  }

  async onTubularChanged(tubular: Tubular | null): Promise<void> {
    if (!tubular) return;

    // Schematic selection events may only carry an id; resolve to the dropdown option object.
    this.setSelectedTubular(tubular.id);

    if (this._appIds?.tubularId === tubular.id) {
      return;
    }

    const success = await firstValueFrom(
      this._userService.updateCurrentAppIds({ tubularId: tubular.id })
    );

    if (success) {
      const appIds = await firstValueFrom(this._userService.getCurrentAppIds());
      this._storeService.setSession(SessionStorageKeys.APP_IDS, appIds);
    }
  }

  toggleSchematic() {
    this._mediator.publish(new ToggleWellSchematicSidebarMessage());
    this._mediator.publish(new GridItemResizedMessage('none', 0, 0));

    this._schematicOpen = !this._schematicOpen;
    this._launcherSharedService.setData(this._schematicOpen);
  }

  openUnitsConverter() {
    this._dialogService.open(UnitsConvertComponent, {
      header: 'Units Converter',
      width: '400px',
      closable: true,
      draggable: true,
      modal: false,
      styleClass: 'popup-dialog',
      style: {
        position: 'absolute',
        top: '75px',
        right: '20px'
      }
    });
  }

  public togglePanelMode(): void {
    this._mediator.publish(new TogglePanelModeMessage());
  }

  public toggleWizardMode(): void {
    this._mediator.publish(new ToggleWizardModeMessage());
  }

  public navigateWizardStep(direction: 'prev' | 'next'): void {
    this._mediator.publish(new WizardStepNavigateMessage(direction));
  }

  public jumpToWizardStep(index: number): void {
    this._mediator.publish(new WizardStepNavigateMessage(index));
  }

  private updatePanelColumnItems(): void {
    this.panelColumnItems = this.panelMode()
      ? [
          { label: '1 Column', command: () => this.setPanelColumns(1) },
          { label: '2 Columns', command: () => this.setPanelColumns(2) },
          { label: '3 Columns', command: () => this.setPanelColumns(3) }
        ]
      : [
          { label: this.compactMode() == true ? 'Turn off Compact Mode' : 'Turn on Compact Mode', command: () => this.toggleCompactMode() }
        ];
  }

  public setPanelColumns(columns: number): void {
    this._mediator.publish(new SetPanelColumnsMessage(columns));
  }

  public toggleCompactMode(): void {
    this._mediator.publish(new ToggleCompactModeMessage(false));
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this._destroy$.next();
    this._destroy$.complete();
    this._signalRHub?.off(SignalRService.ON_PFB_CHANGE, this._signalRHandler);
  }
}
